import { Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CreateClientProfileInput } from '../../types/case-management'

interface FieldRowProps {
  label: string
  children: React.ReactNode
  helper?: string
}

function FieldRow({ label, children, helper }: FieldRowProps): React.ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
        {label}
      </label>
      {children}
      {helper && <p className="mt-1 text-xs text-stone-500">{helper}</p>}
    </div>
  )
}

const INPUT_CLASS =
  'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-[#4e5e82] focus:outline-none focus:ring-1 focus:ring-[#4e5e82] dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100'

interface NameSectionProps {
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
  isAdmin: boolean
  isEdit: boolean
}

function NameSection(props: NameSectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { formData, setFormData, isAdmin, isEdit } = props
  const helper = isEdit && !isAdmin ? t('clients.adminOnlyName') : undefined
  return (
    <FieldRow label={t('clients.formName')} {...(helper ? { helper } : {})}>
      <input
        type="text"
        value={formData.name}
        onChange={(event) => {
          setFormData({ ...formData, name: event.target.value })
        }}
        disabled={isEdit && !isAdmin}
        required
        className={`${INPUT_CLASS} disabled:cursor-not-allowed disabled:bg-stone-100 dark:disabled:bg-stone-700`}
      />
    </FieldRow>
  )
}

interface ShortNameRefSectionProps {
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
}

function ShortNameRefSection(props: ShortNameRefSectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { formData, setFormData } = props
  return (
    <div className="grid grid-cols-2 gap-4">
      <FieldRow label={t('clients.formShortName')}>
        <input
          type="text"
          value={formData.shortName}
          onChange={(event) => {
            setFormData({ ...formData, shortName: event.target.value })
          }}
          className={INPUT_CLASS}
        />
      </FieldRow>
      <FieldRow label={t('clients.formReferenceCode')}>
        <input
          type="text"
          value={formData.referenceCode}
          onChange={(event) => {
            setFormData({ ...formData, referenceCode: event.target.value })
          }}
          placeholder={t('clients.formRefPlaceholder')}
          className={INPUT_CLASS}
        />
      </FieldRow>
    </div>
  )
}

interface ContactSectionProps {
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
}

function ContactSection(props: ContactSectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { formData, setFormData } = props
  return (
    <div className="border-t border-stone-200 pt-4 dark:border-stone-700">
      <h3 className="mb-3 text-sm font-medium text-stone-900 dark:text-stone-100">
        {t('clients.primaryContact')}
      </h3>
      <div className="space-y-3">
        <FieldRow label={t('clients.formContactName')}>
          <input
            type="text"
            value={formData.primaryContactName}
            onChange={(event) => {
              setFormData({ ...formData, primaryContactName: event.target.value })
            }}
            className={INPUT_CLASS}
          />
        </FieldRow>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label={t('clients.formEmail')}>
            <input
              type="email"
              value={formData.primaryContactEmail}
              onChange={(event) => {
                setFormData({ ...formData, primaryContactEmail: event.target.value })
              }}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FieldRow label={t('clients.formPhone')}>
            <input
              type="tel"
              value={formData.primaryContactPhone}
              onChange={(event) => {
                setFormData({ ...formData, primaryContactPhone: event.target.value })
              }}
              className={INPUT_CLASS}
            />
          </FieldRow>
        </div>
      </div>
    </div>
  )
}

interface ModalHeaderProps {
  title: string
  onClose: () => void
}

function ModalHeader({ title, onClose }: ModalHeaderProps): React.ReactElement {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">{title}</h2>
      <button
        onClick={onClose}
        className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

function FormError({ message }: { message: string }): React.ReactElement {
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
      <p className="text-sm text-red-700 dark:text-red-400">{message}</p>
    </div>
  )
}

interface FormFooterProps {
  formLoading: boolean
  isCreate: boolean
  disabled: boolean
  onCancel: () => void
}

function FormFooter(props: FormFooterProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { formLoading, isCreate, disabled, onCancel } = props
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {formLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {isCreate ? t('actions.create') : t('actions.save')}
      </button>
    </div>
  )
}

export interface ClientFormModalProps {
  isCreate: boolean
  formData: CreateClientProfileInput
  setFormData: (data: CreateClientProfileInput) => void
  formError: string | null
  formLoading: boolean
  isAdmin: boolean
  onClose: () => void
  onSubmit: (event: React.FormEvent) => void
}

export function ClientFormModal(props: ClientFormModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { isCreate, formData, setFormData, formError, formLoading, isAdmin, onClose, onSubmit } =
    props
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-stone-900">
        <ModalHeader
          title={isCreate ? t('clients.newClient') : t('clients.editClient')}
          onClose={onClose}
        />
        {formError && <FormError message={formError} />}
        <form onSubmit={onSubmit} className="space-y-4">
          <NameSection
            formData={formData}
            setFormData={setFormData}
            isAdmin={isAdmin}
            isEdit={!isCreate}
          />
          <ShortNameRefSection formData={formData} setFormData={setFormData} />
          <FieldRow label={t('clients.formIndustry')}>
            <input
              type="text"
              value={formData.industry}
              onChange={(event) => {
                setFormData({ ...formData, industry: event.target.value })
              }}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <ContactSection formData={formData} setFormData={setFormData} />
          <FieldRow label={t('clients.formNotes')}>
            <textarea
              value={formData.notes}
              onChange={(event) => {
                setFormData({ ...formData, notes: event.target.value })
              }}
              rows={3}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FormFooter
            formLoading={formLoading}
            isCreate={isCreate}
            disabled={formLoading || !formData.name}
            onCancel={onClose}
          />
        </form>
      </div>
    </div>
  )
}
